import { useCallback, useEffect, useRef, useState } from 'react';
import type { AcceptRequest, CardReplaceRequest, TenantAddonList } from '@/api/generated';
import { ApiError, backendCode } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface AddonsState {
  status: 'loading' | 'ready' | 'error';
  data: TenantAddonList | null;
  error: ApiError | null;
  reload: () => void;
  accept: (id: number, body: AcceptRequest) => Promise<void>;
  decline: (id: number) => Promise<void>;
  cancel: (id: number) => Promise<void>;
  replaceCard: (id: number, body: CardReplaceRequest) => Promise<void>;
  retryCharge: (id: number, chargeId: number) => Promise<void>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/** Loads tenant add-ons. StrictMode-safe. Mutations refetch the list. */
export function useAddons(): AddonsState {
  const [status, setStatus] = useState<AddonsState['status']>('loading');
  const [data, setData] = useState<TenantAddonList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const api = useTenantApi();
  const started = useRef(false);

  const reload = useCallback(() => {
    setStatus('loading');
    setError(null);
    api.listAddons().then(
      (next) => {
        setData(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        setData(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api]);

  const refetch = useCallback(async () => {
    const next = await api.listAddons();
    setData(next);
    setError(null);
    setStatus('ready');
  }, [api]);

  const accept = useCallback(
    async (id: number, body: AcceptRequest) => {
      try {
        await api.acceptAddon(id, body);
      } catch (err) {
        const code = backendCode(err);
        if (code === 'ADDON_STATE' || code === 'ADDON_NETTING_DISABLED') {
          await refetch().catch(() => undefined);
        }
        throw err;
      }
      await refetch();
    },
    [api, refetch],
  );

  const decline = useCallback(
    async (id: number) => {
      await api.declineAddon(id);
      await refetch();
    },
    [api, refetch],
  );

  const cancel = useCallback(
    async (id: number) => {
      await api.cancelAddon(id);
      await refetch();
    },
    [api, refetch],
  );

  const replaceCard = useCallback(
    async (id: number, body: CardReplaceRequest) => {
      await api.replaceAddonCard(id, body);
      await refetch();
    },
    [api, refetch],
  );

  const retryCharge = useCallback(
    async (id: number, chargeId: number) => {
      await api.retryAddonCharge(id, chargeId);
      await refetch();
    },
    [api, refetch],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    reload();
  }, [reload]);

  return { status, data, error, reload, accept, decline, cancel, replaceCard, retryCharge };
}
